import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppIcon from '../../components/AppIcon';
import client from '../../api/client';
import { Loader2, Printer, X, Utensils, ZoomIn, ZoomOut, LayoutGrid } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import FloorPlan from '../../components/seating/FloorPlan';
import GuestSearch from '../../components/seating/GuestSearch';
import GuestPool from '../../components/seating/GuestPool';
import Palette from '../../components/seating/Palette';
import PrintLayout from '../../components/seating/PrintLayout';
import SeatFinderCard from '../../components/seating/SeatFinderCard';
import { TablePanel, FeaturePanel } from '../../components/seating/TablePanel';
import { tableKey, seatNames, plusOneComing, plusOneLabel, SHAPES, FEATURES, freeSpot, lastFirst } from '../../components/seating/seatingUtils';
import { downloadPdf } from '../../components/compliance/ConsentProofButton';

const newId = () => `f${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export default function Seating() {
  const { toast } = useToast();
  const [tables, setTables] = useState([]);
  const [guests, setGuests] = useState([]);
  const [room, setRoom] = useState({ features: [], floorplan_url: null, floorplan_opacity: 0.5 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [showPrint, setShowPrint] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showLayout, setShowLayout] = useState(false);
  const [cardBack, setCardBack] = useState(null);
  const roomRef = useRef(room);
  roomRef.current = room;

  useEffect(() => {
    (async () => {
      const [{ data: s }, { data: g }] = await Promise.all([client.get('/seating'), client.get('/guests')]);
      setTables(s.tables.map((t, i) => ({ shape: 'round', rotation: 0, ...t, x: Number.isFinite(Number(t.x)) ? Number(t.x) : 12 + (i % 6) * 15, y: Number.isFinite(Number(t.y)) ? Number(t.y) : 15 + Math.floor(i / 6) * 22 })));
      setRoom({ features: [], floorplan_url: null, floorplan_opacity: 0.5, ...(s.room || {}) });
      setCardBack(s.card_back || null);
      setGuests(g);
      setLoading(false);
    })();
  }, []);

  const saveRoom = useCallback(async (next) => {
    try { await client.put('/seating/room', next || roomRef.current); }
    catch (e) { toast({ title: 'Could not save the room', description: e.message, variant: 'destructive' }); }
  }, [toast]);
  const patchRoom = (patch) => { const next = { ...roomRef.current, ...patch }; setRoom(next); saveRoom(next); };

  const updateTable = async (id, patch) => {
    const before = tables.find((t) => t.id === id);
    setTables((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    try { await client.put(`/seating/tables/${id}`, patch); } catch (e) {
      if (patch.label !== undefined) setTables((prev) => prev.map((t) => (t.id === id ? { ...t, label: before?.label } : t)));
      toast({ title: 'Could not save table', description: e.message, variant: 'destructive' });
      return;
    }
    if (patch.label !== undefined) {
      const [ok, nk] = [tableKey(before?.label), tableKey(patch.label)];
      if (ok && nk && ok !== nk) setGuests((prev) => prev.map((g) => (String(g.table || '').trim() === ok ? { ...g, table: nk } : g)));
    }
  };

  const occupied = () => [...tables, ...room.features.filter((f) => !FEATURES[f.type]?.area)].map((p) => ({ x: Number(p.x), y: Number(p.y) }));
  const nextLabel = (shape) => {
    if (shape === 'sweetheart') return 'Sweetheart';
    const nums = tables.map((t) => Number(tableKey(t.label))).filter(Number.isFinite);
    return `Table ${(nums.length ? Math.max(...nums) : 0) + 1}`;
  };
  const addTable = async (shape = 'round', base) => {
    const spot = base ? { x: Math.min(95, base.x + 8), y: Math.min(95, base.y + 8) } : freeSpot(occupied());
    const body = { label: nextLabel(shape), seats: base?.seats || SHAPES[shape].defaultSeats, guests: [], shape, rotation: base?.rotation || 0, x: spot.x, y: spot.y };
    const { data } = await client.post('/seating/tables', body);
    setTables((prev) => [...prev, data]);
    setSelected({ kind: 'table', id: data.id });
  };
  const removeTable = async (t) => {
    if (!window.confirm(`Delete ${t.label}? Guests at it go back to "Not seated".`)) return;
    const key = tableKey(t.label);
    setTables((prev) => prev.filter((x) => x.id !== t.id));
    setSelected(null);
    await client.delete(`/seating/tables/${t.id}`).catch(() => {});
    guests.filter((g) => String(g.table || '').trim() === key).forEach((g) => setGuestTable(g.id, ''));
  };

  const setGuestTable = async (guestId, key) => {
    setGuests((prev) => prev.map((g) => (g.id === guestId ? { ...g, table: String(key || '') } : g)));
    try { await client.put(`/guests/${guestId}`, { table: String(key || '') }); }
    catch (e) { toast({ title: 'Could not save seat', description: e.message, variant: 'destructive' }); }
  };

  const addFeature = (type) => {
    const m = FEATURES[type];
    const spot = m.area ? { x: 50, y: 50 } : freeSpot(occupied());
    const f = { id: newId(), type, label: '', x: spot.x, y: spot.y, w: m.w, h: m.h };
    patchRoom({ features: m.area ? [f, ...room.features] : [...room.features, f] });
    setSelected({ kind: 'feature', id: f.id });
  };
  const changeFeature = (f) => setRoom((r) => ({ ...r, features: r.features.map((x) => (x.id === f.id ? f : x)) }));

  const active = guests.filter((g) => g.rsvp !== 'No');
  const byKey = useMemo(() => {
    const m = {};
    active.forEach((g) => { const k = String(g.table || '').trim(); if (k) (m[k] = m[k] || []).push(g); });
    return m;
  }, [active]);
  const tableByKey = useMemo(() => Object.fromEntries(tables.map((t) => [tableKey(t.label), t])), [tables]);
  const namesFor = useCallback((t) => [...(byKey[tableKey(t.label)] || []).flatMap(seatNames), ...(t.guests || [])], [byKey]);
  const unseated = active.filter((g) => !tableByKey[String(g.table || '').trim()]);
  const tableLabelFor = (g) => tableByKey[String(g.table || '').trim()]?.label;

  // Legacy print cards expect guest rows grouped by table key, plus-ones included.
  const printGroups = useMemo(() => {
    const m = {};
    Object.entries(byKey).forEach(([k, list]) => {
      m[k] = list.flatMap((g) => [g, ...(plusOneComing(g) ? [{ ...g, id: `${g.id}-plusone`, name: plusOneLabel(g), isPlusOne: true, hostName: g.name, unnamed: !(g.plusOneName || '').trim() }] : [])]);
    });
    return m;
  }, [byKey]);

  const findGuest = (g) => {
    const t = tableByKey[String(g.table || '').trim()];
    if (!t) { toast({ title: `${g.name} isn't seated yet`, description: 'Select a table, then search for them to seat them.' }); return; }
    setSelected({ kind: 'table', id: t.id });
    setHighlight(t.id);
    setTimeout(() => setHighlight(null), 2600);
    document.querySelector(`[data-testid="reception-table-${t.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  };

  const seatAt = (g, t) => {
    const used = namesFor(t).length;
    const need = plusOneComing(g) ? 2 : 1;
    if (used + need > Number(t.seats)) toast({ title: `${t.label} is now over capacity`, description: `${used + need} people for ${t.seats} seats. Add seats or move someone.` });
    setGuestTable(g.id, tableKey(t.label));
  };

  if (loading) return <div className="flex items-center gap-2 text-[#6A6A6A]"><Loader2 className="w-4 h-4 animate-spin" /> Loading seating…</div>;

  const selTable = selected?.kind === 'table' ? tables.find((t) => t.id === selected.id) : null;
  const selFeature = selected?.kind === 'feature' ? room.features.find((f) => f.id === selected.id) : null;
  const seatedTotal = active.filter((g) => tableByKey[String(g.table || '').trim()]).reduce((n, g) => n + (plusOneComing(g) ? 2 : 1), 0);
  const seatsTotal = tables.reduce((n, t) => n + Number(t.seats || 0), 0);

  return (
    <div className="animate-float-up">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-4xl text-[#2A2A2A] mb-1 flex items-center gap-3"><AppIcon feature="seating" size="lg" reveal testId="section-icon-seating" />Seating plan</h1>
          <p className="text-[#6A6A6A]" data-testid="seating-summary">{seatedTotal} seated · {unseated.reduce((n, g) => n + (plusOneComing(g) ? 2 : 1), 0)} not seated · {seatsTotal} seats at {tables.length} tables</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="seating-print-layout-btn" onClick={() => setShowLayout(true)} className="text-sm px-4 py-2 rounded-full inline-flex items-center gap-2 bg-white border border-[#B8896C] text-[#7B2C3B] hover:bg-[#F4E4DA]"><LayoutGrid className="w-4 h-4" /> Print layout</button>
          <button data-testid="seating-easel-pdf-btn" onClick={async () => { try { const d = new Date(); await downloadPdf(`/seating/easel.pdf?t=${d.getTime()}&origin=${encodeURIComponent(window.location.origin)}`, `find-your-seat-24x36-${d.toISOString().slice(0, 16).replace(/[-:T]/g, '')}.pdf`); } catch (e) { toast({ title: 'Could not create the poster', description: e.message, variant: 'destructive' }); } }} className="text-sm px-4 py-2 rounded-full inline-flex items-center gap-2 bg-white border border-[#B8896C] text-[#7B2C3B] hover:bg-[#F4E4DA]"><Printer className="w-4 h-4" /> Easel poster 24×36 (PDF)</button>
          <button data-testid="seating-place-cards-pdf-btn" onClick={async () => { try { const d = new Date(); await downloadPdf(`/seating/place-cards.pdf?t=${d.getTime()}`, `place-cards-${d.toISOString().slice(0, 16).replace(/[-:T]/g, '')}.pdf`); } catch (e) { toast({ title: 'Could not create place cards', description: e.message, variant: 'destructive' }); } }} className="text-sm px-4 py-2 rounded-full inline-flex items-center gap-2 bg-white border border-[#B8896C] text-[#7B2C3B] hover:bg-[#F4E4DA]"><Printer className="w-4 h-4" /> Place cards (PDF)</button>
          <button data-testid="seating-print-cards-btn" onClick={() => setShowPrint(true)} className="text-sm px-4 py-2 rounded-full inline-flex items-center gap-2 bg-white border border-[#B8896C] text-[#7B2C3B] hover:bg-[#F4E4DA]"><Printer className="w-4 h-4" /> Table cards</button>
          <button data-testid="seating-print-menu-btn" onClick={() => setShowMenu(true)} className="text-sm px-4 py-2 rounded-full inline-flex items-center gap-2 bg-white border border-[#B8896C] text-[#7B2C3B] hover:bg-[#F4E4DA]"><Utensils className="w-4 h-4" /> Menu cards</button>
        </div>
      </div>

      <SeatFinderCard toast={toast} />
      <div className="soft-card rounded-2xl p-4 bg-white border border-[#EFE3D8] mb-4 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4 items-start">
        <Palette onAddTable={(s) => addTable(s)} onAddFeature={addFeature} room={room} onRoom={patchRoom} toast={toast} />
        <div>
          <p className="text-[11px] uppercase tracking-wide text-[#6A6A6A] mb-1">Find a guest</p>
          <GuestSearch testId="seating-find-guest" guests={active} onPick={findGuest} tableLabelFor={tableLabelFor} placeholder="Type any part of a name" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-4 items-start">
        <div className="min-w-0">
          <div className="flex items-center justify-end gap-1 mb-2">
            <button data-testid="seating-zoom-out" aria-label="Zoom out" onClick={() => setZoom((z) => Math.max(0.6, +(z - 0.2).toFixed(1)))} className="p-1.5 rounded-full border border-[#EFE3D8] bg-white"><ZoomOut className="w-4 h-4" /></button>
            <span className="text-xs text-[#6A6A6A] w-10 text-center">{Math.round(zoom * 100)}%</span>
            <button data-testid="seating-zoom-in" aria-label="Zoom in" onClick={() => setZoom((z) => Math.min(2.4, +(z + 0.2).toFixed(1)))} className="p-1.5 rounded-full border border-[#EFE3D8] bg-white"><ZoomIn className="w-4 h-4" /></button>
          </div>
          <FloorPlan tables={tables} features={room.features} room={room} namesFor={namesFor} selected={selected} highlight={highlight} zoom={zoom}
            onSelect={setSelected} onMoveTable={(id, p) => updateTable(id, p)} onChangeFeature={changeFeature} onCommitFeatures={() => saveRoom()}
            onDropGuest={(gid, t) => { const g = guests.find((x) => x.id === gid); if (g) seatAt(g, t); }} />
          <p className="text-[11px] text-[#9A8A7E] mt-2">Drag tables, room features and areas to match your venue. Click a table to seat guests.</p>
        </div>
        <div className="space-y-4 lg:sticky lg:top-4">
        <div className="soft-card rounded-2xl p-5 bg-white border border-[#EFE3D8]">
          {selTable ? (
            <TablePanel table={selTable} seated={byKey[tableKey(selTable.label)] || []} manual={selTable.guests || []} used={namesFor(selTable).length}
              guests={active} tableLabelFor={tableLabelFor}
              onUpdate={(p) => updateTable(selTable.id, p)} onSeat={(g) => seatAt(g, selTable)} onUnseat={(g) => setGuestTable(g.id, '')}
              onAddName={(n) => updateTable(selTable.id, { guests: [...(selTable.guests || []), n] })}
              onRemoveName={(i) => updateTable(selTable.id, { guests: selTable.guests.filter((_, j) => j !== i) })}
              onDelete={() => removeTable(selTable)} onDuplicate={() => addTable(selTable.shape || 'round', selTable)} />
          ) : selFeature ? (
            <FeaturePanel feature={selFeature} onChange={changeFeature} onCommit={() => saveRoom()}
              onDelete={() => { patchRoom({ features: room.features.filter((f) => f.id !== selFeature.id) }); setSelected(null); }} />
          ) : (
            <div data-testid="seating-empty-panel" className="text-sm text-[#6A6A6A] space-y-2">
              <p className="font-serif text-xl text-[#2A2A2A]">Design your room</p>
              <p>1. Add tables, a dance floor, bar and other features from the bar above.</p>
              <p>2. Drag everything into place. Add Indoor or Outdoor areas, or upload the venue floor plan to trace over.</p>
              <p>3. Click a table and type a name to seat guests, or drag names from the list below onto a table.</p>
            </div>
          )}
        </div>
        <div className="soft-card rounded-2xl p-4 bg-white border border-[#EFE3D8]">
          <GuestPool guests={unseated} onPick={(g) => selTable && seatAt(g, selTable)} />
        </div>
        </div>
      </div>

      {showLayout && <PrintLayout tables={tables} features={room.features} room={room} namesFor={namesFor} onClose={() => setShowLayout(false)} />}
      {showPrint && <PrintTableCards tables={tables} guestsByTableNum={printGroups} tableNumOf={tableKey} onClose={() => setShowPrint(false)} updateTable={updateTable} cardBack={cardBack} setCardBack={setCardBack} toast={toast} />}
      {showMenu && <PrintMenuCards tables={tables} guests={active} guestsByTableNum={printGroups} tableNumOf={tableKey} onClose={() => setShowMenu(false)} />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Printable centerpiece cards — one table per page, elegant serif style.
// A modal with an in-browser preview + Print button that opens the browser
// print dialog. Print CSS sizes each card to a full page.
// -----------------------------------------------------------------------------
function PrintTableCards({ tables, guestsByTableNum, tableNumOf, onClose, updateTable, cardBack, setCardBack, toast }) {
  const [selectedIds, setSelectedIds] = useState(() => new Set(tables.map(t => t.id)));
  const [busy, setBusy] = useState(false);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(tables.map(t => t.id)));
  const selectNone = () => setSelectedIds(new Set());

  // Names to print for a table = union of guest-list guests and manual guests
  const namesFor = (t) => {
    const num = tableNumOf(t.label);
    // Sorted by last name, shown "Last, First"; an unnamed plus-one sits right after their host.
    const rows = [
      ...(num ? guestsByTableNum[num] || [] : []).map((g) => (g.unnamed
        ? { display: `Guest of ${g.hostName}`, key: `${lastFirst(g.hostName).key} ~` }
        : lastFirst(g.name))),
      ...(t.guests || []).map(lastFirst),
    ];
    const seen = new Set();
    return rows.filter((r) => r.display && !seen.has(r.key) && seen.add(r.key))
      .sort((a, b) => a.key.localeCompare(b.key)).map((r) => r.display);
  };

  const cards = tables
    .filter(t => selectedIds.has(t.id))
    .map(t => ({ id: t.id, label: t.label, number: tableNumOf(t.label), names: namesFor(t), photo: t.card_photo }));

  const doPrint = () => {
    window.print();
  };
  const downloadCards = async () => {
    setBusy(true);
    try {
      const d = new Date();
      await downloadPdf(`/seating/table-cards.pdf?ids=${cards.map((c) => c.id).join(',')}&t=${d.getTime()}`, `table-cards-${d.toISOString().slice(0, 16).replace(/[-:T]/g, '')}.pdf`);
    } catch (e) { toast({ title: 'Could not create the PDF', description: e.message, variant: 'destructive' }); }
    setBusy(false);
  };
  const upload = async (kind, file) => {
    const fd = new FormData(); fd.append('file', file);
    const { data } = await client.post(`/uploads/${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data;
  };
  const setPhoto = async (t, file) => {
    try { const up = await upload('photo', file); await updateTable(t.id, { card_photo: up.url }); }
    catch (e) { toast({ title: 'Could not upload the picture', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
  };
  const setBack = async (file) => {
    if (file.type !== 'application/pdf') { toast({ title: 'Please choose a PDF file', variant: 'destructive' }); return; }
    try {
      const up = await upload('document', file);
      const { data } = await client.put('/seating/card-back', { filename: up.filename });
      setCardBack(data.card_back);
      if (!data.card_back.is_5x7) toast({ title: 'Back side is not 5″ × 7″', description: `It is ${data.card_back.width_in}″ × ${data.card_back.height_in}″, so it will be scaled to fit the card.` });
    } catch (e) { toast({ title: 'Could not upload the back side', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
  };
  const clearBack = async () => { await client.delete('/seating/card-back'); setCardBack(null); };

  // ESC key closes; also lock body scroll while modal is open so users
  // can't get confused about what's scrolling underneath.
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #print-table-cards, #print-table-cards * { visibility: visible !important; }
          #print-table-cards { position: absolute; left: 0; top: 0; width: 100%; }
          .print-hidden { visibility: hidden !important; }
          .table-card-page {
            width: 5in !important;
            height: 7in !important;
            page-break-after: always;
            break-after: page;
            box-shadow: none !important;
            border: 1.5px dashed #8A8A8A !important;
            border-radius: 0 !important;
            margin: 1.95in auto 0 auto !important;
            overflow: visible !important;
          }
          .table-card-page:last-child { page-break-after: auto; break-after: auto; }
          .table-card-page::before {
            content: '✂  cut along the dotted line';
            position: absolute; top: -0.28in; left: 0;
            font: 8pt Georgia, serif; color: #8A8A8A;
          }
          @page { size: letter portrait; margin: 0; }
        }
      `}</style>

      <div
        className="print-hidden fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="table-cards-backdrop"
      >
        <div
          className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4 border-b border-[#EFE3D8] flex items-center justify-between gap-3 flex-shrink-0 bg-white">
            <div className="min-w-0">
              <h2 className="font-serif text-xl text-[#2A2A2A]">Print table cards</h2>
              <p className="text-xs text-[#6A6A6A]">5″ × 7″ cards centred on 8.5″ × 11″ paper with a dotted cut line. Each table can have its own faded background picture. With a back side uploaded, the PDF alternates front and back pages: print double-sided (flip on long edge) at 100% scale.</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button data-testid="print-cards-select-all" onClick={selectAll} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] hover:border-[#B8896C]">Select all</button>
              <button data-testid="print-cards-select-none" onClick={selectNone} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] hover:border-[#B8896C]">Clear</button>
              <button data-testid="print-cards-download-pdf" onClick={downloadCards} disabled={busy || !cards.length} className="btn-primary text-xs px-4 py-1.5 rounded-full inline-flex items-center gap-1.5 disabled:opacity-50">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />} {cardBack ? 'Two-sided PDF' : 'Download PDF'} {cards.length ? `(${cards.length})` : ''}
              </button>
              <button data-testid="print-cards-print" onClick={doPrint} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] hover:border-[#B8896C]">
                Print fronts
              </button>
              <button data-testid="print-cards-close" onClick={onClose} title="Close (Esc)" className="text-[#9A8A7E] hover:text-[#7B2C3B] p-1"><X className="w-5 h-5" /></button>
            </div>
          </div>

          <div className="p-4 border-b border-[#EFE3D8] bg-[#FBF7F2]">
            <p className="text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-2">Which tables?</p>
            <div className="flex flex-wrap gap-2">
              {tables.map((t) => {
                const on = selectedIds.has(t.id);
                return (
                  <button
                    key={t.id}
                    data-testid={`print-cards-toggle-${t.id}`}
                    onClick={() => toggle(t.id)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? 'bg-[#7B2C3B] text-[#FBF7F2] border-[#7B2C3B]' : 'bg-white text-[#3A3A3A] border-[#EFE3D8] hover:border-[#B8896C]'}`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex items-center gap-3 flex-wrap text-xs" data-testid="card-back-section">
              <span className="text-[10px] uppercase tracking-wider text-[#9A8A7E]">Back side (5″ × 7″ PDF, same on every card)</span>
              {cardBack ? (
                <>
                  <a data-testid="card-back-name" href={`${process.env.REACT_APP_BACKEND_URL}${cardBack.url}`} target="_blank" rel="noreferrer" className="text-[#7B2C3B] underline truncate max-w-[220px]">{cardBack.name}</a>
                  {!cardBack.is_5x7 && <span data-testid="card-back-size-warning" className="text-[#B45309]">{cardBack.width_in}″ × {cardBack.height_in}″, scaled to fit</span>}
                  <label className="cursor-pointer px-3 py-1 rounded-full border border-[#EFE3D8] hover:border-[#B8896C]">Replace<input data-testid="card-back-replace-input" type="file" accept="application/pdf" className="hidden" onChange={(e) => e.target.files[0] && setBack(e.target.files[0])} /></label>
                  <button data-testid="card-back-remove" onClick={clearBack} className="text-[#9A8A7E] hover:text-[#7B2C3B]">Remove</button>
                </>
              ) : (
                <label className="cursor-pointer px-3 py-1 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2]">Upload back side PDF<input data-testid="card-back-upload-input" type="file" accept="application/pdf" className="hidden" onChange={(e) => e.target.files[0] && setBack(e.target.files[0])} /></label>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-[#F6EAE0]/40">
            <div id="print-table-cards" data-testid="print-table-cards-preview">
              {cards.length === 0 && (
                <p className="text-center text-sm text-[#6A6A6A] py-16">Select at least one table to preview a card.</p>
              )}
              {cards.map((c) => (
                <div key={c.id}>
                  <TableCard label={c.label} number={c.number} names={c.names} photo={c.photo} />
                  <div className="print-hidden flex justify-center gap-2 -mt-3 mb-6 text-xs">
                    <label className="cursor-pointer px-3 py-1 rounded-full bg-white border border-[#EFE3D8] hover:border-[#B8896C]">{c.photo ? 'Change picture' : 'Add background picture'}<input data-testid={`card-photo-input-${c.id}`} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => e.target.files[0] && setPhoto(c, e.target.files[0])} /></label>
                    {c.photo && <button data-testid={`card-photo-remove-${c.id}`} onClick={() => updateTable(c.id, { card_photo: '' })} className="px-3 py-1 rounded-full text-[#9A8A7E] hover:text-[#7B2C3B]">Remove picture</button>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sticky bottom bar — always in reach even if the huge 10.5in
              preview pushes the top header out of view. */}
          <div className="p-3 border-t border-[#EFE3D8] flex items-center justify-between gap-2 flex-shrink-0 bg-white">
            <button data-testid="print-cards-back" onClick={onClose} className="text-xs px-4 py-2 rounded-full border border-[#EFE3D8] hover:border-[#B8896C] text-[#3A3A3A]">
              ← Back to seating
            </button>
            <button data-testid="print-cards-print-bottom" onClick={downloadCards} disabled={busy || !cards.length} className="btn-primary text-xs px-4 py-2 rounded-full inline-flex items-center gap-1.5 disabled:opacity-50">
              <Printer className="w-3.5 h-3.5" /> {cardBack ? 'Two-sided PDF' : 'Download PDF'} {cards.length ? `(${cards.length})` : ''}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function TableCard({ label, number, names, photo }) {
  const n = names.length;
  const size = n <= 8 ? 17 : n <= 11 ? 15 : n <= 14 ? 13 : n <= 28 ? 11 : 9;
  const numeric = /^\d+$/.test(String(number || ''));
  return (
    <div data-testid="table-card-5x7" className="table-card-page mx-auto my-6 bg-[#FBF7F2] border border-[#EFE3D8] shadow-sm rounded-lg overflow-hidden flex flex-col items-center text-center relative" style={{ width: '5in', height: '7in', padding: '0.4in 0.45in' }}>
      {photo && <img data-testid="table-card-photo" src={`${process.env.REACT_APP_BACKEND_URL}${photo}`} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" style={{ opacity: 0.25 }} />}
      <div data-testid="table-card-number" className="relative font-bold leading-none" style={{ fontFamily: "Georgia, 'Times New Roman', serif", color: '#8A6A32', fontSize: numeric ? '1.9in' : '0.55in', marginTop: numeric ? '0' : '0.45in' }}>
        {number || label}
      </div>
      <div className="relative mt-3 flex items-center gap-3 text-[#B8896C]">
        <span className="block w-16 h-px bg-[#B8896C]" />
        <span className="text-base" style={{ fontFamily: "'Cormorant Garamond', serif" }}>♥</span>
        <span className="block w-16 h-px bg-[#B8896C]" />
      </div>
      <ul className="relative mt-4 w-full flex-1 overflow-hidden" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", columnCount: n > 14 ? 2 : 1, columnGap: '0.25in' }}>
        {n === 0 && <li className="italic text-[#9A8A7E]" style={{ fontSize: '13pt' }}>No guests assigned yet</li>}
        {names.map((nm, i) => (
          <li key={i} className="text-[#2A2A2A]" style={{ fontSize: `${size}pt`, lineHeight: 1.3 }}>{nm}</li>
        ))}
      </ul>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Printable menu cards — two modes toggle in the modal:
//   • by-guest → one card per guest (Name + meal + dietary) — place setting
//   • by-table → one card per table listing everyone + their meals — kitchen ref
// Both use the same elegant serif print CSS as the table cards.
// -----------------------------------------------------------------------------
function PrintMenuCards({ tables, guests, guestsByTableNum, tableNumOf, onClose }) {
  const [mode, setMode] = useState('by-guest');
  const [selectedTableIds, setSelectedTableIds] = useState(() => new Set(tables.map(t => t.id)));

  const toggle = (id) => {
    setSelectedTableIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedTableIds(new Set(tables.map(t => t.id)));
  const selectNone = () => setSelectedTableIds(new Set());

  // Build the list of (guest, tableLabel, tableNum) rows for cards.
  const rows = useMemo(() => {
    const includedNums = new Set(
      tables.filter(t => selectedTableIds.has(t.id)).map(t => tableNumOf(t.label))
    );
    return (guests || [])
      .filter(g => {
        const t = String(g.table || '').trim();
        return t && includedNums.has(t);
      })
      .flatMap(g => {
        const tableLabel = tables.find(t => tableNumOf(t.label) === String(g.table))?.label || `Table ${g.table}`;
        const own = { name: g.name, meal: g.meal && g.meal !== '—' ? g.meal : '', dietary: g.dietary || '', tableLabel, tableNum: g.table };
        if (g.plusOne && g.plusOneName) {
          const plus = { name: g.plusOneName, meal: '', dietary: '', tableLabel, tableNum: g.table };
          return [own, plus];
        }
        return [own];
      });
  }, [guests, tables, selectedTableIds, tableNumOf]);

  // For by-table mode: group rows by tableNum
  const groupedByTable = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (!map.has(r.tableNum)) map.set(r.tableNum, { label: r.tableLabel, num: r.tableNum, guests: [] });
      map.get(r.tableNum).guests.push(r);
    });
    return Array.from(map.values()).sort((a, b) => Number(a.num) - Number(b.num));
  }, [rows]);

  const doPrint = () => window.print();

  // Same ESC + body-scroll-lock UX as the table-cards modal.
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #print-menu-cards, #print-menu-cards * { visibility: visible !important; }
          #print-menu-cards { position: absolute; left: 0; top: 0; width: 100%; }
          .print-hidden { visibility: hidden !important; }
          .menu-card-page {
            width: 100%;
            min-height: 100vh;
            page-break-after: always;
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
          }
          .menu-card-page:last-child { page-break-after: auto; }
          @page { size: letter portrait; margin: 0.5in; }
        }
      `}</style>

      <div
        className="print-hidden fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        data-testid="menu-cards-modal"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4 border-b border-[#EFE3D8] flex items-center justify-between gap-3 flex-shrink-0 bg-white">
            <div className="min-w-0">
              <h2 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                <Utensils className="w-5 h-5 text-[#7B2C3B]" /> Print menu cards
              </h2>
              <p className="text-xs text-[#6A6A6A]">Elegant serif cards for each place setting or a kitchen-side sheet per table.</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button data-testid="menu-cards-print" onClick={doPrint} className="btn-primary text-xs px-4 py-1.5 rounded-full inline-flex items-center gap-1.5">
                <Printer className="w-3.5 h-3.5" /> Print {rows.length ? `(${mode === 'by-guest' ? rows.length : groupedByTable.length})` : ''}
              </button>
              <button data-testid="menu-cards-close" onClick={onClose} title="Close (Esc)" className="text-[#9A8A7E] hover:text-[#7B2C3B] p-1"><X className="w-5 h-5" /></button>
            </div>
          </div>

          <div className="p-4 border-b border-[#EFE3D8] bg-[#FBF7F2] space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-2">Layout</p>
              <div className="inline-flex rounded-full border border-[#EFE3D8] p-0.5 bg-white">
                <button
                  data-testid="menu-mode-by-guest"
                  onClick={() => setMode('by-guest')}
                  className={`text-xs px-3 py-1.5 rounded-full ${mode === 'by-guest' ? 'bg-[#7B2C3B] text-[#FBF7F2]' : 'text-[#3A3A3A]'}`}
                >
                  One card per guest
                </button>
                <button
                  data-testid="menu-mode-by-table"
                  onClick={() => setMode('by-table')}
                  className={`text-xs px-3 py-1.5 rounded-full ${mode === 'by-table' ? 'bg-[#7B2C3B] text-[#FBF7F2]' : 'text-[#3A3A3A]'}`}
                >
                  One sheet per table (kitchen)
                </button>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wider text-[#9A8A7E]">Which tables?</p>
                <div className="flex items-center gap-1.5">
                  <button data-testid="menu-cards-select-all" onClick={selectAll} className="text-[10px] px-2 py-0.5 rounded-full border border-[#EFE3D8] hover:border-[#B8896C]">All</button>
                  <button data-testid="menu-cards-select-none" onClick={selectNone} className="text-[10px] px-2 py-0.5 rounded-full border border-[#EFE3D8] hover:border-[#B8896C]">None</button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {tables.map((t) => {
                  const on = selectedTableIds.has(t.id);
                  return (
                    <button
                      key={t.id}
                      data-testid={`menu-cards-toggle-${t.id}`}
                      onClick={() => toggle(t.id)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? 'bg-[#7B2C3B] text-[#FBF7F2] border-[#7B2C3B]' : 'bg-white text-[#3A3A3A] border-[#EFE3D8] hover:border-[#B8896C]'}`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-[#F6EAE0]/40">
            <div id="print-menu-cards" data-testid="menu-cards-preview">
              {rows.length === 0 && (
                <p className="text-center text-sm text-[#6A6A6A] py-16">No guests with a table assignment yet. Set table numbers on the guest list first.</p>
              )}
              {mode === 'by-guest' && rows.map((r, i) => (
                <MenuCardGuest key={`${r.name}-${i}`} {...r} />
              ))}
              {mode === 'by-table' && groupedByTable.map((g) => (
                <MenuCardTable key={g.num} label={g.label} number={g.num} guests={g.guests} />
              ))}
            </div>
          </div>

          <div className="p-3 border-t border-[#EFE3D8] flex items-center justify-between gap-2 flex-shrink-0 bg-white">
            <button data-testid="menu-cards-back" onClick={onClose} className="text-xs px-4 py-2 rounded-full border border-[#EFE3D8] hover:border-[#B8896C] text-[#3A3A3A]">
              ← Back to seating
            </button>
            <button data-testid="menu-cards-print-bottom" onClick={doPrint} className="btn-primary text-xs px-4 py-2 rounded-full inline-flex items-center gap-1.5">
              <Printer className="w-3.5 h-3.5" /> Print {rows.length ? `(${mode === 'by-guest' ? rows.length : groupedByTable.length})` : ''}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function MenuCardGuest({ name, meal, dietary, tableLabel }) {
  return (
    <div className="menu-card-page mx-auto my-6 bg-[#FBF7F2] border border-[#EFE3D8] shadow-sm rounded-lg overflow-hidden flex flex-col items-center justify-center text-center relative" style={{ width: '5in', minHeight: '7in', padding: '0.6in 0.5in' }}>
      <div className="flex items-center gap-3 text-[#B8896C]">
        <span className="block w-10 h-px bg-[#B8896C]" />
        <span className="text-[10px] tracking-[0.4em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{tableLabel}</span>
        <span className="block w-10 h-px bg-[#B8896C]" />
      </div>

      <div className="mt-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        <div className="font-normal leading-tight text-[#2A2A2A]" style={{ fontSize: '2.6rem', letterSpacing: '-0.01em' }}>
          {name}
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 text-[#B8896C]">
        <span className="block w-16 h-px bg-[#B8896C]" />
        <span style={{ fontFamily: "'Cormorant Garamond', serif" }} className="italic text-base">menu</span>
        <span className="block w-16 h-px bg-[#B8896C]" />
      </div>

      <div className="mt-6 space-y-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        {meal ? (
          <p className="text-[#2A2A2A]" style={{ fontSize: '1.4rem' }}>{meal}</p>
        ) : (
          <p className="italic text-[#9A8A7E]" style={{ fontSize: '1.15rem' }}>Chef&apos;s selection</p>
        )}
        {dietary && (
          <p className="italic text-[#7B2C3B]" style={{ fontSize: '0.95rem' }}>{dietary}</p>
        )}
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#B8896C]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        <span className="text-xl">♥</span>
      </div>
    </div>
  );
}

function MenuCardTable({ label, number, guests }) {
  return (
    <div className="menu-card-page mx-auto my-6 bg-[#FBF7F2] border border-[#EFE3D8] shadow-sm rounded-lg overflow-hidden relative" style={{ width: '8in', minHeight: '10.5in', padding: '0.75in 0.9in' }}>
      <div className="flex items-center gap-3 text-[#B8896C] justify-center">
        <span className="block w-12 h-px bg-[#B8896C]" />
        <span className="text-xs tracking-[0.4em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Table</span>
        <span className="block w-12 h-px bg-[#B8896C]" />
      </div>
      <div className="mt-3 text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        <div className="font-bold leading-none" style={{ color: '#8A6A32', fontSize: '5rem' }}>{number || label}</div>
      </div>
      <div className="mt-6 flex items-center gap-3 text-[#B8896C] justify-center">
        <span className="block w-20 h-px bg-[#B8896C]" />
        <span style={{ fontFamily: "'Cormorant Garamond', serif" }} className="italic text-lg">menu · kitchen sheet</span>
        <span className="block w-20 h-px bg-[#B8896C]" />
      </div>

      <table className="mt-6 w-full" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        <thead>
          <tr className="border-b border-[#B8896C]">
            <th className="text-left px-2 py-2 text-[#7B2C3B] text-xs uppercase tracking-wider font-medium">Guest</th>
            <th className="text-left px-2 py-2 text-[#7B2C3B] text-xs uppercase tracking-wider font-medium">Meal</th>
            <th className="text-left px-2 py-2 text-[#7B2C3B] text-xs uppercase tracking-wider font-medium">Dietary</th>
          </tr>
        </thead>
        <tbody>
          {guests.map((g, i) => (
            <tr key={i} className="border-b border-[#EFE3D8]">
              <td className="px-2 py-2.5 text-[#2A2A2A]" style={{ fontSize: '1.15rem' }}>{g.name}</td>
              <td className="px-2 py-2.5 text-[#2A2A2A]" style={{ fontSize: '1.05rem' }}>{g.meal || '—'}</td>
              <td className="px-2 py-2.5 italic text-[#7B2C3B]" style={{ fontSize: '0.95rem' }}>{g.dietary || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#B8896C]">
        <span className="text-xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>♥</span>
      </div>
    </div>
  );
}

